import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import OperationLoader from '@components/Common/Loaders/OperationLoader';
import { useAuth } from '@components/MsalProvider';
import { Info } from '@components/Common/SharedStyles';
import TankBalance from '@components/Common/TankBalance';
import { formatLocalDateTime } from '@helpers/dateUtils';
import { useTerminalsQuery } from '@api/queries/terminals';
import {
  useTanksQuery,
  useTankBalancesQuery,
  useTankSurfaceLevelsQuery,
  combineTanksWithBalances,
  Tank,
} from '@api/queries/tanks';

const TanksContainer = styled.div`
  display: flex;
  flex-direction: horizontal;
  flex-wrap: wrap;
`;

const TankBox = styled.div`
  display: inline-block;
  border: 1px solid #666;
  border-radius: 10px;
  padding: 15px;
  width: 300px;
  margin: 15px;

  cursor: pointer;

  h4 {
    margin-bottom: 5px;
  }

  &:hover {
    box-shadow: 0 0 1px 1px rgb(68, 152, 231, 60%);
    h4 {
      text-decoration: underline;
    }
  }
`;

const TankHeadingContainer = styled.div`
  display: flex;
  flex-direction: horizontal;

  > div:nth-child(2) {
    flex: 1;
    text-align: right;
  }
`;

const MainHeading = styled.h2`
  text-align: center;
  margin: 0 0 25px 0;
`;

const TerminalHeading = styled.h4`
  margin: 15px 0;
  padding-bottom: 5px;
  border-bottom: 1px solid #ccc;
  text-align: center;
`;

const BalanceHeading = styled.h5`
  margin: 10px 0 5px 0;
  font-size: 14px;
  font-weight: normal;
`;

const InfoContainer = styled.div`
  width: 400;
  margin: 0 auto;
  text-align: center;
`;

const Loader = styled.div`
  display: inline-block;
  position: absolute;
  margin-top: 3px;
  margin-left: 10px;
`;

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Get user's assigned terminals from auth context
  const { terminals: userTerminals } = useAuth();

  // Fetch data using TanStack Query
  const { data: allTerminals = [], isLoading: isLoadingTerminals } = useTerminalsQuery();
  const { data: tanksData = [], isLoading: isLoadingTanks } = useTanksQuery();
  const { data: balances, isLoading: isLoadingBalances } = useTankBalancesQuery();
  const { data: surfaceLevels, isLoading: isLoadingSurfaceLevels } = useTankSurfaceLevelsQuery();

  // Combined loading state
  const isLoading = isLoadingTerminals || isLoadingTanks || isLoadingBalances || isLoadingSurfaceLevels;

  // Filter terminals to only show user's assigned terminals
  const terminals = useMemo(
    () => allTerminals.filter((t) => userTerminals.includes(t.id)),
    [allTerminals, userTerminals]
  );

  // Combine tanks with balances and surface levels
  const tanks = useMemo(
    () => combineTanksWithBalances(tanksData, balances, surfaceLevels),
    [tanksData, balances, surfaceLevels]
  );

  const openBookkeepingEntries = (tank: Tank) => {
    // Navigate to bookkeeping with tank filter in URL params
    const params = new URLSearchParams();
    params.set('terminal', tank.terminalId);
    params.set('tank', tank.id);
    navigate(`/bookkeeping?${params.toString()}`);
  };

  return (
    <>
      <MainHeading>
        Wibax Logistics - {t('dashboard.heading')}
        {isLoading && (
          <Loader>
            <OperationLoader title={t('updating') + '...'} />
          </Loader>
        )}
      </MainHeading>
      {terminals.length === 0 && !isLoading && (
        <InfoContainer>
          <Info>{t('dashboard.no_default_terminal')}</Info>
        </InfoContainer>
      )}

      {terminals
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((terminal) => (
          <div key={terminal.id}>
            <TerminalHeading>{terminal.name}</TerminalHeading>
            <TanksContainer>
              {tanks
                .filter((tank) => tank.terminalId === terminal.id)
                .filter((tank) => tank.isEnabled)
                .filter((tank) => tank.productId)
                .filter((tank) => tank.customers && tank.customers.length > 0)
                .map((tank) => (
                  <TankView key={tank.id} tank={tank} onClick={() => openBookkeepingEntries(tank)} t={t} />
                ))}
            </TanksContainer>
          </div>
        ))}
    </>
  );
};

interface TankViewProps {
  tank: Tank;
  onClick: (tank: Tank) => void;
  t: any;
}

const TankView: React.FC<TankViewProps> = ({ tank, onClick, t }) => {
  return (
    <TankBox onClick={() => onClick(tank)}>
      <TankHeadingContainer>
        <h4>{tank.name}</h4>
        <div>{tank.productName}</div>
      </TankHeadingContainer>
      <div>
        <em>{tank.customers.map(c => c.customerName).join(', ')}</em>
      </div>
      {!!tank.balance && (
        <>
          <BalanceHeading>
            <small>
              {t('dashboard.bookkeeping')} {formatLocalDateTime(tank.balance.timestamp, true)}
            </small>
          </BalanceHeading>
          <TankBalance tankBalance={tank.balance} textWidth={120} showUnit={true} />
        </>
      )}
      {!!tank.surfaceLevel && (
        <>
          <BalanceHeading>
            <small>
              {t('dashboard.surface_radar')} {formatLocalDateTime(tank.surfaceLevel.timestamp, true)}
              {typeof tank.surfaceLevel.temperature === 'number' && ` @ ${tank.surfaceLevel.temperature} °C`}
            </small>
          </BalanceHeading>
          <TankBalance tankBalance={tank.surfaceLevel} textWidth={120} showUnit={true} />
        </>
      )}
    </TankBox>
  );
};

export default Dashboard;
